import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import {
  ArrowDown,
  ArrowUp,
  CheckCircle2,
  FileStack,
  Files,
  LoaderCircle,
  Play,
  ScanLine,
  Trash2,
  UploadCloud,
} from 'lucide-react';
import { useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import { api } from '../lib/api';

interface UploadedFile {
  id: string;
  originalName: string;
  mimeType: string;
  sizeBytes: number;
}

interface PlanJob {
  id: string;
  status: 'QUEUED' | 'RUNNING' | 'COMPLETED' | 'FAILED' | 'CANCELLED';
  progress: number;
  input: { titleAr?: string; attachmentIds?: string[] };
  output: {
    fileCount?: number;
    totalPages?: number;
    merged?: boolean;
    outputAttachmentId?: string;
    outputName?: string;
    verificationUrl?: string;
  } | null;
  createdAt: string;
  events: Array<{ messageAr: string }>;
}

interface CoverTemplate {
  id: string;
  key: string;
  nameAr: string;
  descriptionAr: string;
  primaryColor: string;
}

interface StampPreset {
  id: string;
  key: string;
  nameAr: string;
  descriptionAr: string;
}

const statusLabels = {
  QUEUED: 'في الانتظار',
  RUNNING: 'جارٍ الفحص',
  COMPLETED: 'مكتملة',
  FAILED: 'فاشلة',
  CANCELLED: 'ملغاة',
} as const;

export function PlanProcessingPage() {
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const inputRef = useRef<HTMLInputElement>(null);
  const [title, setTitle] = useState('');
  const [coverTemplateKey, setCoverTemplateKey] = useState('details_classic');
  const [stampPresetKey, setStampPresetKey] = useState('qr_standard');
  const [details, setDetails] = useState({
    drawingType: '',
    ownerName: '',
    officeName: 'Details للاستشارات الهندسية',
    issueDate: new Date().toISOString().slice(0, 10),
    coverImageAttachmentId: '',
  });
  const [files, setFiles] = useState<UploadedFile[]>([]);
  const [uploading, setUploading] = useState(false);

  const plansQuery = useQuery({
    queryKey: ['plans'],
    queryFn: () => api<PlanJob[]>('/api/plans'),
    refetchInterval: 4_000,
  });
  const presetsQuery = useQuery({
    queryKey: ['plan-presets'],
    queryFn: () =>
      api<{ coverTemplates: CoverTemplate[]; stampPresets: StampPreset[] }>(
        '/api/plans/presets',
      ),
  });

  const createPlan = useMutation({
    mutationFn: () =>
      api<PlanJob>('/api/plans', {
        method: 'POST',
        body: JSON.stringify({
          titleAr: title,
          attachmentIds: files.map((file) => file.id),
          coverTemplateKey,
          stampPresetKey,
          projectName: title,
          ...(details.drawingType ? { drawingType: details.drawingType } : {}),
          ...(details.ownerName ? { ownerName: details.ownerName } : {}),
          ...(details.officeName ? { officeName: details.officeName } : {}),
          ...(details.issueDate ? { issueDate: details.issueDate } : {}),
          ...(details.coverImageAttachmentId
            ? { coverImageAttachmentId: details.coverImageAttachmentId }
            : {}),
        }),
      }),
    onSuccess: async () => {
      toast.success('بدأ فحص حزمة المخططات');
      await queryClient.invalidateQueries({ queryKey: ['plans'] });
      navigate('/app/jobs');
    },
    onError: (error) => toast.error(error.message),
  });

  async function uploadFiles(selected: FileList | null) {
    if (!selected?.length) return;
    if (files.length + selected.length > 20) {
      toast.error('الحد الأقصى 20 ملفًا في الحزمة');
      return;
    }
    setUploading(true);
    try {
      const uploaded: UploadedFile[] = [];
      for (const file of [...selected]) {
        const form = new FormData();
        form.append('file', file);
        uploaded.push(
          await api<UploadedFile>('/api/files/upload', {
            method: 'POST',
            body: form,
          }),
        );
      }
      setFiles((current) => [...current, ...uploaded]);
      if (!title && uploaded[0]) setTitle(uploaded[0].originalName.replace(/\.[^.]+$/, ''));
      const firstImage = uploaded.find((file) =>
        ['image/png', 'image/jpeg'].includes(file.mimeType),
      );
      if (firstImage && !details.coverImageAttachmentId) {
        setDetails((current) => ({
          ...current,
          coverImageAttachmentId: firstImage.id,
        }));
      }
      toast.success(`رُفع ${uploaded.length} ملف للمعالجة المؤقتة`);
    } catch (error) {
      toast.error(error instanceof Error ? error.message : 'تعذر رفع الملفات');
    } finally {
      setUploading(false);
      if (inputRef.current) inputRef.current.value = '';
    }
  }

  const totalBytes = files.reduce((sum, file) => sum + file.sizeBytes, 0);
  return (
    <div className="min-h-screen p-4 sm:p-8">
      <div className="mx-auto max-w-6xl">
        <header className="mb-7">
          <p className="text-xs font-black text-teal">معالجة محلية أولًا</p>
          <h1 className="mt-2 text-2xl font-black">مركز تجهيز المخططات</h1>
          <p className="mt-2 max-w-2xl text-xs font-semibold leading-6 text-slate-500">
            ارفع ملفات الحزمة، أعد ترتيبها، ثم أنشئ PDF مغلفًا بغلاف عربي
            وQR تحقق وبصمة أرشيفية.
          </p>
        </header>

        <div className="grid gap-5 lg:grid-cols-[1fr_340px]">
          <section className="rounded-3xl border border-ink/10 bg-white p-5 sm:p-7">
            <label className="block">
              <span className="mb-2 block text-[11px] font-black text-ink">اسم الحزمة</span>
              <input
                value={title}
                onChange={(event) => setTitle(event.target.value)}
                placeholder="مثال: مخططات مشروع فيلا"
                className="w-full rounded-xl border border-ink/10 bg-slate-50 px-4 py-3 text-sm font-semibold outline-none focus:border-teal focus:ring-4 focus:ring-teal/10"
              />
            </label>

            <div className="mt-4 grid gap-3 sm:grid-cols-2">
              <label>
                <span className="mb-2 block text-[11px] font-black text-ink">قالب الغلاف</span>
                <select
                  value={coverTemplateKey}
                  onChange={(event) => setCoverTemplateKey(event.target.value)}
                  className="w-full rounded-xl border border-ink/10 bg-slate-50 px-4 py-3 text-xs font-semibold outline-none focus:border-teal"
                >
                  {presetsQuery.data?.coverTemplates.map((template) => (
                    <option key={template.key} value={template.key}>{template.nameAr}</option>
                  ))}
                </select>
              </label>
              <label>
                <span className="mb-2 block text-[11px] font-black text-ink">ختم التحقق</span>
                <select
                  value={stampPresetKey}
                  onChange={(event) => setStampPresetKey(event.target.value)}
                  className="w-full rounded-xl border border-ink/10 bg-slate-50 px-4 py-3 text-xs font-semibold outline-none focus:border-teal"
                >
                  {presetsQuery.data?.stampPresets.map((preset) => (
                    <option key={preset.key} value={preset.key}>{preset.nameAr}</option>
                  ))}
                </select>
              </label>
            </div>

            <div className="mt-4 rounded-2xl border border-ink/10 bg-slate-50/70 p-4">
              <h2 className="mb-3 text-[11px] font-black text-ink">بيانات المشروع على الغلاف</h2>
              <div className="grid gap-3 sm:grid-cols-2">
                <input
                  value={details.drawingType}
                  onChange={(event) => setDetails({ ...details, drawingType: event.target.value })}
                  placeholder="نوع المخطط"
                  className="rounded-xl border border-ink/10 bg-white px-3 py-2.5 text-xs outline-none focus:border-teal"
                />
                <input
                  value={details.ownerName}
                  onChange={(event) => setDetails({ ...details, ownerName: event.target.value })}
                  placeholder="اسم المالك"
                  className="rounded-xl border border-ink/10 bg-white px-3 py-2.5 text-xs outline-none focus:border-teal"
                />
                <input
                  value={details.officeName}
                  onChange={(event) => setDetails({ ...details, officeName: event.target.value })}
                  placeholder="المكتب"
                  className="rounded-xl border border-ink/10 bg-white px-3 py-2.5 text-xs outline-none focus:border-teal"
                />
                <input
                  type="date"
                  value={details.issueDate}
                  onChange={(event) => setDetails({ ...details, issueDate: event.target.value })}
                  className="rounded-xl border border-ink/10 bg-white px-3 py-2.5 text-xs outline-none focus:border-teal"
                />
                <label className="sm:col-span-2">
                  <span className="mb-1.5 block text-[9px] font-black text-slate-500">صورة الغلاف</span>
                  <select
                    value={details.coverImageAttachmentId}
                    onChange={(event) => setDetails({ ...details, coverImageAttachmentId: event.target.value })}
                    className="w-full rounded-xl border border-ink/10 bg-white px-3 py-2.5 text-xs outline-none focus:border-teal"
                  >
                    <option value="">بدون صورة خلفية</option>
                    {files
                      .filter((file) => ['image/png', 'image/jpeg'].includes(file.mimeType))
                      .map((file) => (
                        <option key={file.id} value={file.id}>{file.originalName}</option>
                      ))}
                  </select>
                </label>
              </div>
            </div>

            <button
              type="button"
              onClick={() => inputRef.current?.click()}
              disabled={uploading}
              className="mt-5 flex min-h-52 w-full flex-col items-center justify-center rounded-2xl border-2 border-dashed border-teal/25 bg-teal/5 px-5 text-center transition hover:border-teal/50 hover:bg-teal/10"
            >
              {uploading ? (
                <LoaderCircle className="h-9 w-9 animate-spin text-teal" />
              ) : (
                <UploadCloud className="h-9 w-9 text-teal" />
              )}
              <span className="mt-4 text-sm font-black text-ink">
                {uploading ? 'جارٍ رفع الملفات…' : 'اختيار ملفات المخططات'}
              </span>
              <span className="mt-2 text-[10px] font-semibold text-slate-500">
                PDF أو صور — حتى 20 ملفًا — والملفات مؤقتة لمدة 24 ساعة
              </span>
            </button>
            <input
              ref={inputRef}
              type="file"
              multiple
              accept=".pdf,.png,.jpg,.jpeg"
              className="hidden"
              onChange={(event) => void uploadFiles(event.target.files)}
            />

            {files.length > 0 && (
              <div className="mt-5 space-y-2">
                {files.map((file, index) => (
                  <div key={file.id} className="flex items-center gap-3 rounded-xl border border-ink/10 bg-slate-50 p-3">
                    <span className="grid h-8 w-8 place-items-center rounded-lg bg-white text-[9px] font-black text-teal">
                      {String(index + 1).padStart(2, '0')}
                    </span>
                    <div className="min-w-0 flex-1">
                      <p className="truncate text-xs font-black">{file.originalName}</p>
                      <p className="mt-0.5 text-[9px] font-bold text-slate-400">
                        {(file.sizeBytes / 1024 / 1024).toFixed(2)} م.ب
                      </p>
                    </div>
                    <div className="flex gap-1">
                      <button
                        type="button"
                        onClick={() =>
                          setFiles((current) => {
                            if (index === 0) return current;
                            const next = [...current];
                            [next[index - 1], next[index]] = [next[index]!, next[index - 1]!];
                            return next;
                          })
                        }
                        disabled={index === 0}
                        className="rounded-lg p-1.5 text-slate-400 hover:bg-white hover:text-teal disabled:opacity-20"
                        aria-label="تحريك لأعلى"
                      >
                        <ArrowUp className="h-3.5 w-3.5" />
                      </button>
                      <button
                        type="button"
                        onClick={() =>
                          setFiles((current) => {
                            if (index === current.length - 1) return current;
                            const next = [...current];
                            [next[index], next[index + 1]] = [next[index + 1]!, next[index]!];
                            return next;
                          })
                        }
                        disabled={index === files.length - 1}
                        className="rounded-lg p-1.5 text-slate-400 hover:bg-white hover:text-teal disabled:opacity-20"
                        aria-label="تحريك لأسفل"
                      >
                        <ArrowDown className="h-3.5 w-3.5" />
                      </button>
                    </div>
                    <button
                      type="button"
                      onClick={() => {
                        setFiles((current) => current.filter((item) => item.id !== file.id));
                        if (details.coverImageAttachmentId === file.id) {
                          setDetails((current) => ({
                            ...current,
                            coverImageAttachmentId: '',
                          }));
                        }
                      }}
                      className="rounded-lg p-2 text-slate-400 hover:bg-red-50 hover:text-red-600"
                      aria-label={`إزالة ${file.originalName}`}
                    >
                      <Trash2 className="h-3.5 w-3.5" />
                    </button>
                  </div>
                ))}
                <div className="flex items-center justify-between rounded-xl bg-ink px-4 py-3 text-white">
                  <span className="text-[10px] font-bold text-white/55">إجمالي الحزمة</span>
                  <span className="text-xs font-black">
                    {files.length} ملف · {(totalBytes / 1024 / 1024).toFixed(2)} م.ب
                  </span>
                </div>
              </div>
            )}

            <button
              type="button"
              onClick={() => createPlan.mutate()}
              disabled={!title.trim() || files.length === 0 || uploading || createPlan.isPending}
              className="mt-5 inline-flex w-full items-center justify-center gap-2 rounded-xl bg-teal px-5 py-3 text-sm font-black text-white shadow-lg shadow-teal/15 hover:bg-ink disabled:cursor-not-allowed disabled:opacity-40"
            >
              {createPlan.isPending ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <Play className="h-4 w-4" />}
              بدء فحص الحزمة
            </button>
          </section>

          <aside className="space-y-4">
            <section className="rounded-2xl border border-ink/10 bg-white p-5">
              <h2 className="text-sm font-black">مسار المعالجة</h2>
              <div className="mt-4 space-y-4">
                {[
                  { label: 'رفع مؤقت', text: 'ملفات الحزمة تبقى على الخادم مؤقتًا.', Icon: UploadCloud, active: true },
                  { label: 'فحص محلي', text: 'نوع الملف، الحجم، البصمة وعدد الصفحات.', Icon: ScanLine, active: true },
                  { label: 'تجهيز البيانات', text: 'بناء manifest قابل للتتبع.', Icon: FileStack, active: false },
                  { label: 'الغلاف والتحقق', text: 'PDF مجمع، غلاف عربي وQR تحقق.', Icon: Files, active: true },
                ].map(({ label, text, Icon, active }, index) => (
                  <div key={label} className="flex gap-3">
                    <div className={`grid h-9 w-9 shrink-0 place-items-center rounded-xl ${
                      active ? 'bg-teal text-white' : 'bg-slate-100 text-slate-400'
                    }`}>
                      <Icon className="h-4 w-4" />
                    </div>
                    <div>
                      <p className="text-[11px] font-black">{index + 1}. {label}</p>
                      <p className="mt-1 text-[9px] font-semibold leading-4 text-slate-500">{text}</p>
                    </div>
                  </div>
                ))}
              </div>
            </section>

            <section className="rounded-2xl border border-ink/10 bg-white p-5">
              <h2 className="text-sm font-black">آخر الحزم</h2>
              <div className="mt-3 space-y-2">
                {plansQuery.data?.slice(0, 5).map((job) => (
                  <div key={job.id} className="rounded-xl border border-ink/5 bg-slate-50 p-3">
                    <div className="flex items-center justify-between gap-2">
                      <p className="truncate text-[10px] font-black">{job.input.titleAr || 'حزمة مخططات'}</p>
                      {job.status === 'COMPLETED' && <CheckCircle2 className="h-3.5 w-3.5 text-emerald-600" />}
                    </div>
                    <p className="mt-1 text-[8px] font-bold text-slate-400">
                      {statusLabels[job.status]} · {job.output?.fileCount || job.input.attachmentIds?.length || 0} ملف
                      {job.output?.totalPages ? ` · ${job.output.totalPages} صفحة` : ''}
                    </p>
                    {job.output?.outputAttachmentId && (
                      <a
                        href={`/api/files/${job.output.outputAttachmentId}`}
                        className="mt-2 inline-block text-[9px] font-black text-teal hover:underline"
                      >
                        تنزيل PDF المجمع
                      </a>
                    )}
                    {job.output?.verificationUrl && (
                      <a
                        href={job.output.verificationUrl}
                        target="_blank"
                        rel="noreferrer"
                        className="ms-3 mt-2 inline-block text-[9px] font-black text-gold hover:underline"
                      >
                        فتح سجل التحقق
                      </a>
                    )}
                  </div>
                ))}
                {!plansQuery.data?.length && (
                  <p className="py-6 text-center text-[10px] font-bold text-slate-400">لا توجد حزم سابقة.</p>
                )}
              </div>
            </section>
          </aside>
        </div>
      </div>
    </div>
  );
}
